import { ensureOpenAnswersProperties } from "../../features/project_migration/OpenAnswersMigration";
import { hasOpenAnswers } from "../../models/OpenAnswers";
import { findQuizFeedbackInteraction } from "./helpers/QuizFeedbackLocator";
import { getDeletedPropIds, getPropReferences } from "../../helpers/PropReferences";
import { matchTakeStartToPreviousTakeEnd } from "../../helpers/TakeEndState";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ImageBounds } from "../../features/training_preview/ImageBounds";
import {
  Actor,
  normalizeActorForCollection,
  normalizeActors,
} from "../../models/Actor";
import { ActorState } from "../../models/ActorState";
import { InteractionProperty } from "../../models/InteractionProperty";
import Interaction, {
  InteractionType,
} from "../../models/interactions/Interaction";
import {
  getEnvironmentPropPositionId,
  Prop,
  PropType,
} from "../../models/Prop";
import { IFormQuiz, QuizFeedbackSlot } from "../../models/Quiz";
import Take from "../../models/Take";
import Take360 from "../../models/Take360";
import Take3D, {
  getTake3DSequence,
} from "../../models/Take3D";
import { RootState } from "../Store";
import { TrainingSliceStrategy } from "./strategies/TrainingSliceStrategy";
import { TrainingSliceStrategy360 } from "./strategies/TrainingSliceStrategy360";
import { TrainingSliceStrategy3D } from "./strategies/TrainingSliceStrategy3D";
import { ITransition } from "../../models/interactions/Transition";
import { Variable } from "../../models/interactions/Variable";
import {
  getReservedSequenceVariables,
  isReservedSequenceVariableId,
  isReservedSequenceVariableName,
  normalizeSequenceVariables,
} from "../../models/interactions/BuiltInSequenceVariables";
import { ISequence } from "../../models/interactions/SequenceModel";
import {
  InteractionMovePayload,
  InteractionsMovePayload,
} from "./helpers/InteractionMove";
import {
  resolveSelectionContainer,
  selectionContainersEqual,
} from "./helpers/SelectionContainer";
import { MoveSequenceSelectionPayload } from "./helpers/SequenceMove";
import { selectDocumentIsDirty } from "./HistorySlice";

export enum TrainingType {
  Training3D = 0,
  Training360 = 1,
  NotSet = -1,
}

export interface TrainingState {
  trainingName: string; // re-usable
  trainingDescription: string; // re-usable

  takes360: Take360[]; // different
  takes3D: Take3D[]; // different

  selectedTake: string; // re-usable
  selectedElementId: string; // re-usable
  selectedElementIsTake: boolean; // re-usable
  selectedElementIsInteraction: boolean; // re-usable
  selectedSequenceTakeId: string; // active 3D take-owned sequence editor

  // Ordered multi-selection of top-level interactions in the selected Take.
  // Invariant: contains the primary (selectedElementId) whenever the primary
  // is a top-level interaction of the selected Take, and is empty otherwise.
  selectedInteractionIds: string[];
  selectionAnchorId: string; // shift-range anchor within the multi-selection

  filter: string; // re-usable

  viewportBounds: ImageBounds | undefined; // re-usable
  imageBounds: ImageBounds | undefined; // re-usable

  isPlaying: boolean; // re-usable
  testData: string; // re-usable

  referenceLanguage: string; // shortcode, re-usable

  isDirty: boolean; // re-usable

  trainingType: TrainingType; // re-usable

  actors: Actor[]; // re-usable => these are the actor presets
  selectedActor: string; // re-usable

  props: Prop[]; // a list of props used in the training
  selectedProp: string; // the currently selected prop
  selectedEnvironmentLayerIds: string[]; // selected 3D environment layers

  sequenceVariables: Variable[]; // project-wide variables

  disableProgressBar: boolean; // project-wide setting
}

export type Take3DCopyPayload = {
  take: Take3D;
  transition: ITransition;
  interactions?: Interaction[];
  sequence?: ISequence;
};

export const initialState: TrainingState = {
  trainingName: "",
  trainingDescription: "",
  takes360: [],
  takes3D: [],
  selectedTake: "",
  selectedElementId: "",
  selectedElementIsTake: true,
  selectedElementIsInteraction: false,
  selectedSequenceTakeId: "",
  selectedInteractionIds: [],
  selectionAnchorId: "",
  filter: "",
  isPlaying: false,
  viewportBounds: undefined,
  imageBounds: undefined,
  testData: "",
  referenceLanguage: "",
  isDirty: false,
  trainingType: TrainingType.NotSet,
  actors: [],
  selectedActor: "",
  props: [],
  selectedProp: "",
  selectedEnvironmentLayerIds: [],
  sequenceVariables: [],
  disableProgressBar: false,
};

export const selectTakeIndexById = (takes: Take[], takeId: string) => {
  return takes.findIndex((take) => take.id === takeId);
};

export const selectSelectedTakeActors = createSelector(
  [
    (state: RootState) => state.training.actors,
    (state: RootState) => state.training.takes3D,
    (state: RootState) => state.training.selectedTake,
  ],
  (actors, takes3D, selectedTake) => {
    //At least the player character is always filled in
    const activeTakeActors: Actor[] = [
      actors.find((actor) => actor.id === "player")!,
    ];
    //Find the currently selected take
    const tempSelectedTake: Take3D | undefined = takes3D.find(
      (take) => take.id === selectedTake
    );

    //Match current take actor states with global list of actors
    if (tempSelectedTake !== undefined) {
      const allActors: Actor[] = actors;

      tempSelectedTake.actors.forEach((actorState) => {
        const foundActor = allActors.find((a) => a.id === actorState.actorId);
        if (foundActor) activeTakeActors.push(foundActor);
      });
    }

    return [...new Set(activeTakeActors)];
  }
);

export const selectSelectedTakeProps = createSelector(
  [
    (state: RootState) => state.training.takes3D,
    (state: RootState) => state.training.selectedTake,
  ],
  (takes3D, selectedTake) => {
    const take = takes3D.find((t) => t.id === selectedTake);
    return take?.props ?? [];
  }
);

export const selectInteractionIndexById = (
  take: Take,
  interactionId: string
) => {
  return take.interactions.findIndex(
    (interaction) => interaction.id === interactionId
  );
};

const resolveTrainingStrategy = (
  trainingType: TrainingType
): TrainingSliceStrategy | undefined => {
  switch (trainingType) {
    case TrainingType.Training360:
      return TrainingSliceStrategy360;
    case TrainingType.Training3D:
      return TrainingSliceStrategy3D;
    default:
      return undefined;
  }
};

const warnMissingStrategy = (context: string, trainingType: TrainingType) => {
  if (process.env.NODE_ENV === "production") return;

  console.warn(
    `[trainingSlice] ${context} skipped because no strategy is available for trainingType=${trainingType}.`
  );
};

const getActiveTakes = (state: TrainingState): Take[] =>
  state.trainingType === TrainingType.Training360
    ? state.takes360
    : state.takes3D;

const stringArraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

// Preloaded or persisted states written before multi-selection existed may
// lack the selection fields; heal them before reducers read them.
const ensureSelectionFields = (state: TrainingState) => {
  if (!Array.isArray(state.selectedInteractionIds)) {
    state.selectedInteractionIds = [];
  }
  if (typeof state.selectionAnchorId !== "string") {
    state.selectionAnchorId = "";
  }
};

// Adding an interaction moves the primary to the new row; any existing
// multi-selection must collapse rather than absorb it.
const resetInteractionMultiSelection = (state: TrainingState) => {
  state.selectedInteractionIds = [];
  state.selectionAnchorId = "";
};

// Re-establishes the multi-selection invariant: selectedInteractionIds mirrors
// the primary's container order (a Take's top-level list or one quiz feedback
// slot), contains the primary, and is empty whenever the primary is not an
// interaction (Take, transition, teleport marker, sequence). Assignments are
// guarded so unrelated reducers keep the array's identity stable.
const normalizeInteractionMultiSelection = (state: TrainingState) => {
  ensureSelectionFields(state);
  const container = state.selectedElementIsInteraction
    ? resolveSelectionContainer(getActiveTakes(state), state.selectedElementId)
    : undefined;
  const orderedIds = container
    ? container.interactions.map((interaction) => interaction.id)
    : [];

  if (!orderedIds.includes(state.selectedElementId)) {
    if (state.selectedInteractionIds.length > 0) {
      state.selectedInteractionIds = [];
    }
    if (state.selectionAnchorId) state.selectionAnchorId = "";
    return;
  }

  const members = orderedIds.filter(
    (id) =>
      id === state.selectedElementId ||
      state.selectedInteractionIds.includes(id)
  );
  if (!stringArraysEqual(state.selectedInteractionIds, members)) {
    state.selectedInteractionIds = members;
  }
  if (!members.includes(state.selectionAnchorId)) {
    state.selectionAnchorId = state.selectedElementId;
  }
};

const applyStrategyPatch = (
  state: TrainingState,
  context: string,
  run: (strategy: TrainingSliceStrategy) => Partial<TrainingState> | undefined
) => {
  const strategy = resolveTrainingStrategy(state.trainingType);
  if (!strategy) {
    warnMissingStrategy(context, state.trainingType);
    return;
  }

  const patch = run(strategy);
  if (patch !== undefined) {
    Object.assign(state, patch);
    normalizeInteractionMultiSelection(state);
  }
};

const runStrategySelector = <T,>(
  rootState: RootState,
  context: string,
  run: (strategy: TrainingSliceStrategy) => T
): T | undefined => {
  const strategy = resolveTrainingStrategy(rootState.training.trainingType);
  if (!strategy) {
    warnMissingStrategy(context, rootState.training.trainingType);
    return undefined;
  }

  return run(strategy);
};

const normalizePropPreset = (prop: Prop): Prop => ({
  ...prop,
  hideInPropLibrary: prop.hideInPropLibrary ?? false,
});

export const trainingSlice = createSlice({
  name: "training",
  initialState: initialState,
  reducers: {
    createNewTraining: (
      state,
      action: PayloadAction<{
        trainingname: string;
        trainingDescription: string;
        trainingType: TrainingType;
      }>
    ) => {
      state.trainingName = action.payload.trainingname;
      state.trainingDescription = action.payload.trainingDescription;
      state.trainingType = action.payload.trainingType;
      state.takes360 = [];
      state.sequenceVariables = [];
      state.selectedSequenceTakeId = "";
      normalizeInteractionMultiSelection(state);
    },
    setTrainingName: (state, action: PayloadAction<string>) => {
      state.trainingName = action.payload;
    },
    setTrainingDescription: (state, action: PayloadAction<string>) => {
      state.trainingDescription = action.payload;
    },
    setDisableProgressBar: (state, action: PayloadAction<boolean>) => {
      state.disableProgressBar = action.payload;
      state.isDirty = true;
    },
    loadDisableProgressBar: (state, action: PayloadAction<boolean>) => {
      state.disableProgressBar = action.payload;
    },
    setTrainingType: (state, action: PayloadAction<TrainingType>) => {
      state.trainingType = action.payload;
    },
    loadTrainingMetadata: (
      state,
      action: PayloadAction<{
        name: string;
        description: string;
        type: TrainingType;
      }>
    ) => {
      state.trainingName = action.payload.name;
      state.trainingDescription = action.payload.description;
      state.trainingType = action.payload.type;
    },
    setSelectedTake: (state, action: PayloadAction<string>) => {
      applyStrategyPatch(state, "setSelectedTake", (strategy) =>
        strategy.setSelectedTake(state, action.payload)
      );
    },
    setSelectedInteraction: (state, action: PayloadAction<string>) => {
      // Plain selection replaces any multi-selection with just this row.
      resetInteractionMultiSelection(state);
      const takes = getActiveTakes(state);
      const feedback = findQuizFeedbackInteraction(takes, action.payload);
      const selectionId = feedback && hasOpenAnswers(
        takes[feedback.takeIndex].interactions[feedback.quizIndex]
      ) ? feedback.quizId : action.payload;
      applyStrategyPatch(state, "setSelectedInteraction", (strategy) =>
        strategy.setSelectedInteraction(state, selectionId)
      );
    },
    toggleInteractionSelection: (state, action: PayloadAction<string>) => {
      ensureSelectionFields(state);
      const interactionId = action.payload;
      const activeTakes = getActiveTakes(state);
      const container = resolveSelectionContainer(activeTakes, interactionId);
      const currentContainer = resolveSelectionContainer(
        activeTakes,
        state.selectedElementId
      );

      if (
        !container ||
        !selectionContainersEqual(container, currentContainer) ||
        state.selectedInteractionIds.length === 0
      ) {
        // No active multi-selection in this container: behave like a plain
        // click.
        state.selectedInteractionIds = [];
        state.selectionAnchorId = "";
        applyStrategyPatch(state, "toggleInteractionSelection", (strategy) =>
          strategy.setSelectedInteraction(state, interactionId)
        );
        return;
      }

      if (!state.selectedInteractionIds.includes(interactionId)) {
        // Add to the selection; normalization orders it and keeps the members.
        applyStrategyPatch(state, "toggleInteractionSelection", (strategy) =>
          strategy.setSelectedInteraction(state, interactionId)
        );
        state.selectionAnchorId = interactionId;
        return;
      }

      // Removing the sole selected row keeps it selected.
      if (state.selectedInteractionIds.length === 1) return;

      const orderedIds = container.interactions.map(
        (interaction) => interaction.id
      );
      const remaining = state.selectedInteractionIds.filter(
        (id) => id !== interactionId
      );
      let primary = state.selectedElementId;
      if (primary === interactionId) {
        const removedIndex = orderedIds.indexOf(interactionId);
        const after = orderedIds
          .slice(removedIndex + 1)
          .find((id) => remaining.includes(id));
        const before = orderedIds
          .slice(0, removedIndex)
          .reverse()
          .find((id) => remaining.includes(id));
        primary = after ?? before ?? remaining[remaining.length - 1];
      }
      applyStrategyPatch(state, "toggleInteractionSelection", (strategy) =>
        strategy.setSelectedInteraction(state, primary)
      );
      state.selectedInteractionIds = remaining;
      if (!remaining.includes(state.selectionAnchorId)) {
        state.selectionAnchorId = primary;
      }
    },
    selectInteractionRange: (state, action: PayloadAction<string>) => {
      ensureSelectionFields(state);
      const interactionId = action.payload;
      const container = resolveSelectionContainer(
        getActiveTakes(state),
        interactionId
      );
      if (!container) {
        state.selectedInteractionIds = [];
        state.selectionAnchorId = "";
        applyStrategyPatch(state, "selectInteractionRange", (strategy) =>
          strategy.setSelectedInteraction(state, interactionId)
        );
        return;
      }

      // Ids are unique training-wide, so membership in this container's order
      // is also the same-container check for the anchor candidates.
      const orderedIds = container.interactions.map(
        (interaction) => interaction.id
      );
      const anchorId = orderedIds.includes(state.selectionAnchorId)
        ? state.selectionAnchorId
        : orderedIds.includes(state.selectedElementId)
          ? state.selectedElementId
          : interactionId;
      const anchorIndex = orderedIds.indexOf(anchorId);
      const targetIndex = orderedIds.indexOf(interactionId);
      const [start, end] =
        anchorIndex <= targetIndex
          ? [anchorIndex, targetIndex]
          : [targetIndex, anchorIndex];

      applyStrategyPatch(state, "selectInteractionRange", (strategy) =>
        strategy.setSelectedInteraction(state, interactionId)
      );
      state.selectedInteractionIds = orderedIds.slice(start, end + 1);
      state.selectionAnchorId = anchorId;
    },
    setSelectedTakeSequence: (state, action: PayloadAction<string>) => {
      if (state.trainingType !== TrainingType.Training3D) return;

      const take = state.takes3D.find((candidate) => candidate.id === action.payload);
      const sequence = getTake3DSequence(take);
      if (!take || !sequence) return;

      state.selectedTake = take.id;
      state.selectedSequenceTakeId = take.id;
      state.selectedElementId = sequence.id;
      state.selectedElementIsTake = false;
      state.selectedElementIsInteraction = false;
      normalizeInteractionMultiSelection(state);
    },
    setIsPlaying: (
      state,
      action: PayloadAction<{ isPlaying: boolean; data: string }>
    ) => {
      state.isPlaying = action.payload.isPlaying;
      state.testData = action.payload.data;
    },
    addNewTake: (
      state,
      action: PayloadAction<{
        name?: string;
        assetId?: string;
        transition?: ITransition;
        /** 3D only: start from an empty stage instead of inheriting the last take. */
        clean?: boolean;
      }>
    ) => {
      applyStrategyPatch(state, "addNewTake", (strategy) =>
        strategy.addNewTake(
          state,
          action.payload.name,
          action.payload.assetId,
          action.payload.transition,
          action.payload.clean
        )
      );
    },
    addTakeCopy: (
      state,
      action: PayloadAction<Take360 | Take3DCopyPayload>
    ) => {
      const payload = action.payload;
      applyStrategyPatch(state, "addTakeCopy", (strategy) =>
        "take" in payload && "transition" in payload
          ? strategy.addTakeCopy(
              state,
              payload.take,
              payload.transition,
              payload.interactions,
              payload.sequence
            )
          : strategy.addTakeCopy(state, payload as Take360)
      );
    },
    addInteractionToTake: (
      state,
      action: PayloadAction<{ takeId: string; interaction: Interaction }>
    ) => {
      resetInteractionMultiSelection(state);
      applyStrategyPatch(state, "addInteractionToTake", (strategy) =>
        strategy.addInteractionToTake(
          state,
          action.payload.interaction,
          action.payload.takeId
        )
      );
    },
    addInteractionToSelectedTake: (
      state,
      action: PayloadAction<Interaction>
    ) => {
      resetInteractionMultiSelection(state);
      applyStrategyPatch(state, "addInteractionToSelectedTake", (strategy) =>
        strategy.addInteractionToSelectedTake(state, action.payload)
      );
    },
    addInteractionCopy: (state, action: PayloadAction<Interaction>) => {
      resetInteractionMultiSelection(state);
      applyStrategyPatch(state, "addInteractionCopy", (strategy) =>
        strategy.addInteractionCopy(state, action.payload)
      );
    },
    addInteractionToQuizFeedback: (
      state,
      action: PayloadAction<{
        quizId: string;
        slot: QuizFeedbackSlot;
        interaction: Interaction;
      }>
    ) => {
      resetInteractionMultiSelection(state);
      applyStrategyPatch(state, "addInteractionToQuizFeedback", (strategy) =>
        strategy.addInteractionToQuizFeedback(
          state,
          action.payload.quizId,
          action.payload.slot,
          action.payload.interaction
        )
      );
    },
    reorderQuizFeedbackInteractions: (
      state,
      action: PayloadAction<{
        quizId: string;
        slot: QuizFeedbackSlot;
        startIndex: number;
        endIndex: number;
      }>
    ) => {
      applyStrategyPatch(
        state,
        "reorderQuizFeedbackInteractions",
        (strategy) =>
          strategy.reorderQuizFeedbackInteractions(
            state,
            action.payload.quizId,
            action.payload.slot,
            action.payload.startIndex,
            action.payload.endIndex
          )
      );
    },
    deleteInteraction: (state, action: PayloadAction<Interaction>) => {
      applyStrategyPatch(state, "deleteInteraction", (strategy) =>
        strategy.deleteInteraction(state, action.payload)
      );
    },
    // Toggles the test-exclusion flag on top-level and quiz-feedback
    // interactions. Selection state is deliberately untouched: disabled rows
    // must stay selectable so they can be re-enabled.
    setInteractionsDisabled: (
      state,
      action: PayloadAction<{ ids: string[]; disabled: boolean }>
    ) => {
      const { ids, disabled } = action.payload;
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      let changed = false;

      const applyTo = (interaction: Interaction) => {
        if (!idSet.has(interaction.id)) return;
        if (Boolean(interaction.disabled) === disabled) return;
        if (disabled) interaction.disabled = true;
        // Removing the key keeps re-enabled interactions' saved JSON
        // identical to never-disabled ones.
        else delete interaction.disabled;
        changed = true;
      };

      for (const take of getActiveTakes(state)) {
        for (const interaction of take.interactions) {
          applyTo(interaction);
          const quiz = interaction as IFormQuiz;
          quiz.correctFeedbackInteractions?.forEach(applyTo);
          quiz.wrongFeedbackInteractions?.forEach(applyTo);
        }
      }

      if (changed) state.isDirty = true;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    reorderTakes: (
      state,
      action: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      const { startIndex, endIndex } = { ...action.payload };
      applyStrategyPatch(state, "reorderTakes", (strategy) =>
        strategy.reorderTakes(state, startIndex, endIndex)
      );
    },
    reorderInteractions: (
      state,
      action: PayloadAction<{
        startTakeId: string;
        destinationTakeId: string;
        startIndex: number;
        endIndex: number;
      }>
    ) => {
      const { startTakeId, destinationTakeId, startIndex, endIndex } = {
        ...action.payload,
      };

      applyStrategyPatch(state, "reorderInteractions", (strategy) =>
        strategy.reorderInteractions(
          state,
          startTakeId,
          destinationTakeId,
          startIndex,
          endIndex
        )
      );
    },
    moveInteraction: (
      state,
      action: PayloadAction<InteractionMovePayload>
    ) => {
      applyStrategyPatch(state, "moveInteraction", (strategy) =>
        strategy.moveInteraction(state, action.payload)
      );
    },
    moveInteractions: (
      state,
      action: PayloadAction<InteractionsMovePayload>
    ) => {
      applyStrategyPatch(state, "moveInteractions", (strategy) =>
        strategy.moveInteractions(state, action.payload)
      );
    },
    setSelectedInteractions: (state, action: PayloadAction<string[]>) => {
      // Selects a whole group at once (e.g. freshly pasted or duplicated
      // copies). Normalization orders the ids and derives the anchor.
      ensureSelectionFields(state);
      const ids = action.payload.filter(Boolean);
      if (ids.length === 0) return;

      state.selectedInteractionIds = ids;
      applyStrategyPatch(state, "setSelectedInteractions", (strategy) =>
        strategy.setSelectedInteraction(state, ids[0])
      );
      if (state.selectedInteractionIds.includes(ids[0])) {
        state.selectionAnchorId = ids[0];
      }
    },
    setPrimaryInteraction: (state, action: PayloadAction<string>) => {
      // Changes the focused interaction while preserving the multi-selection
      // (e.g. when a group drag starts on a selected row).
      ensureSelectionFields(state);
      if (!state.selectedInteractionIds.includes(action.payload)) {
        state.selectedInteractionIds = [];
        state.selectionAnchorId = "";
      }
      applyStrategyPatch(state, "setPrimaryInteraction", (strategy) =>
        strategy.setSelectedInteraction(state, action.payload)
      );
    },
    moveSequenceSelection: (
      state,
      action: PayloadAction<MoveSequenceSelectionPayload>
    ) => {
      applyStrategyPatch(state, "moveSequenceSelection", (strategy) =>
        strategy.moveSequenceSelection(state, action.payload)
      );
    },
    updateSelectedTake: (state, action: PayloadAction<Take>) => {
      applyStrategyPatch(state, "updateSelectedTake", (strategy) =>
        strategy.updateSelectedTake(state, action.payload)
      );
    },
    updateSelectedTakeProperty: (
      state,
      action: PayloadAction<InteractionProperty>
    ) => {
      applyStrategyPatch(state, "updateSelectedTakeProperty", (strategy) =>
        strategy.updateSelectedTakeProperty(state, action.payload)
      );
    },
    updateSelectedInteraction: (state, action: PayloadAction<Interaction>) => {
      applyStrategyPatch(state, "updateSelectedInteraction", (strategy) =>
        strategy.updateSelectedInteraction(state, action.payload)
      );
    },
    updateSelectedInteractionProperty: (
      state,
      action: PayloadAction<InteractionProperty>
    ) => {
      applyStrategyPatch(state, "updateSelectedInteractionProperty", (strategy) =>
        strategy.updateSelectedInteractionProperty(state, action.payload)
      );
    },
    updateSelectedTakeSequence: (state, action: PayloadAction<ISequence>) => {
      if (state.trainingType !== TrainingType.Training3D) return;

      const takeId = state.selectedSequenceTakeId || state.selectedTake;
      const takeIndex = state.takes3D.findIndex((take) => take.id === takeId);
      if (takeIndex === -1) return;

      state.takes3D[takeIndex] = {
        ...state.takes3D[takeIndex],
        sequence: action.payload,
      };
      state.selectedElementId = action.payload.id;
      state.isDirty = true;
      normalizeInteractionMultiSelection(state);
    },
    setViewportBounds: (state, action: PayloadAction<ImageBounds>) => {
      state.viewportBounds = action.payload;
    },
    setImageBounds: (state, action: PayloadAction<ImageBounds>) => {
      state.imageBounds = action.payload;
    },
    replaceTakesFromUnity: (state, action: PayloadAction<Take[]>) => {
      applyStrategyPatch(state, "replaceTakesFromUnity", (strategy) =>
        strategy.setTakes(state, ensureOpenAnswersProperties(action.payload))
      );
    },
    hydrateTakes: (state, action: PayloadAction<Take[]>) => {
      applyStrategyPatch(state, "hydrateTakes", (strategy) =>
        strategy.setTakes(state, ensureOpenAnswersProperties(action.payload))
      );
      state.isDirty = false;
    },
    deleteTake: (state, action: PayloadAction<string>) => {
      if (!action.payload) return;
      applyStrategyPatch(state, "deleteTake", (strategy) =>
        strategy.deleteTake(state, action.payload)
      );
    },
    clearTakes: (state) => {
      state.takes360 = [];
      state.selectedSequenceTakeId = "";
      normalizeInteractionMultiSelection(state);
    },
    setReferenceLanguage: (state, action: PayloadAction<string>) => {
      state.referenceLanguage = action.payload;
    },
    loadReferenceLanguage: (state, action: PayloadAction<string>) => {
      state.referenceLanguage = action.payload;
    },
    setIsDirty: (state) => {
      state.isDirty = true;
    },
    resetIsDirty: (state) => {
      state.isDirty = false;
    },
    loadActors: (state, action: PayloadAction<Actor[]>) => {
      state.actors = normalizeActors(action.payload);
      if (state.actors.length > 0) state.selectedActor = state.actors[0].id;
    },
    addActor: (state, action: PayloadAction<Actor>) => {
      const normalizedActor = normalizeActorForCollection(
        action.payload,
        state.actors
      );
      state.actors.push(normalizedActor);
      state.selectedActor = normalizedActor.id;

      state.isDirty = true;
    },
    updateActor: (state, action: PayloadAction<Actor>) => {
      const index = state.actors.findIndex(
        (actor) => actor.id === action.payload.id
      );
      if (index > -1) {
        state.actors[index] = normalizeActorForCollection(
          action.payload,
          state.actors
        );
        state.isDirty = true;
      }
    },
    setSelectedActor: (state, action: PayloadAction<string>) => {
      state.selectedActor = action.payload;
    },
    deleteActor: (state, action: PayloadAction<string>) => {
      state.actors = state.actors.filter(
        (actor) => actor.id !== action.payload
      );
      state.isDirty = true;
    },
    loadPropPresets: (state, action: PayloadAction<Prop[]>) => {
      state.props = action.payload.map(normalizePropPreset);
    },
    addPropPreset: (state, action: PayloadAction<Prop>) => {
      const prop = normalizePropPreset(action.payload);
      state.props.push(prop);
      state.selectedProp = prop.id;

      state.isDirty = true;
    },
    addEnvironmentPropPresetToAllTakes: (state, action: PayloadAction<Prop>) => {
      const actionProp = normalizePropPreset(action.payload);
      const environmentMetadataId = actionProp.environmentMetadataId;
      if (!environmentMetadataId || state.trainingType !== TrainingType.Training3D)
        return;

      const existingPreset = state.props.find(
        (prop) =>
          prop.propType === PropType.Environment &&
          prop.environmentMetadataId === environmentMetadataId
      );
      const preset = existingPreset ?? actionProp;

      if (!existingPreset) {
        state.props.push(preset);
      }
      state.selectedProp = preset.id;

      const positionId = getEnvironmentPropPositionId(environmentMetadataId);
      state.takes3D = state.takes3D.map((take) => {
        const hasState = take.props.some(
          (prop) =>
            prop.instanceId === preset.id || prop.positionId === positionId
        );
        if (hasState) return take;

        return {
          ...take,
          props: [
            ...take.props,
            {
              id: `${take.id}:${positionId}`,
              instanceId: preset.id,
              positionId,
            },
          ],
        };
      });

      state.isDirty = true;
    },
    setSelectedPropPreset: (state, action: PayloadAction<string>) => {
      state.selectedProp = action.payload;
    },
    deletePropPreset: (state, action: PayloadAction<string>) => {
      if (getPropReferences(state, action.payload).length > 0) return;
      const deletedIds = getDeletedPropIds(state.props, action.payload);
      const environmentPositionIds = new Set(
        state.props
          .filter((prop) => deletedIds.has(prop.id) && prop.environmentMetadataId)
          .map((prop) => getEnvironmentPropPositionId(prop.environmentMetadataId!))
      );

      state.selectedProp = "";
      state.props = state.props.filter((prop) => !deletedIds.has(prop.id));
      if (environmentPositionIds.size > 0) {
        state.takes3D = state.takes3D.map((take) => ({
          ...take,
          props: take.props.filter(
            (prop) =>
              !deletedIds.has(prop.instanceId) &&
              !environmentPositionIds.has(prop.positionId)
          ),
        }));
      }
      state.isDirty = true;
    },
    setSelectedEnvironmentLayerIds: (state, action: PayloadAction<string[]>) => {
      state.selectedEnvironmentLayerIds = action.payload;
      state.isDirty = true;
    },
    loadSelectedEnvironmentLayerIds: (state, action: PayloadAction<string[]>) => {
      state.selectedEnvironmentLayerIds = action.payload;
    },
    updateSelectedActorState: (state, action: PayloadAction<ActorState>) => {
      applyStrategyPatch(state, "updateSelectedActorState", (strategy) =>
        strategy.updateSelectedActorState(state, action.payload)
      );
    },
    updateSelectedPropPreset: (state, action: PayloadAction<Prop>) => {
      const index = state.props.findIndex(
        (prop) => prop.id === state.selectedProp
      );
      if (index === -1) return;
      state.props[index] = normalizePropPreset(action.payload);
    },
    updatePropPresetPrefabNames: (
      state,
      action: PayloadAction<{ propId: string; prefabName: string }>
    ) => {
      const { propId, prefabName } = action.payload;
      if (!prefabName) return; // never blank a prefab name
      state.props.forEach((prop) => {
        if (prop.propId === propId) prop.prefabName = prefabName;
      });
    },
    addPropStateToSelectedTake: (
      state,
      action: PayloadAction<{ instanceId: string; positionId: string }>
    ) => {
      applyStrategyPatch(state, "addPropStateToSelectedTake", (strategy) =>
        strategy.addPropToSelectedTake(
          state,
          action.payload.instanceId,
          action.payload.positionId
        )
      );
    },
    removePropStateFromSelectedTake: (state, action: PayloadAction<string>) => {
      applyStrategyPatch(state, "removePropStateFromSelectedTake", (strategy) =>
        strategy.removePropFromSelectedTake(state, action.payload)
      );
    },

    // ====== Situations =======
    addSituationToTakeLocation: (
      state,
      action: PayloadAction<{
        takePosition: string;
        situationInstanceId: string;
        fallbackCameraId: string;
        actorsToRemove: string[];
      }>
    ) => {
      // this will take place in the selected take
      applyStrategyPatch(state, "addSituationToTakeLocation", (strategy) =>
        strategy.addSituationToTakeLocation(
          state,
          action.payload.takePosition,
          action.payload.situationInstanceId,
          action.payload.fallbackCameraId,
          action.payload.actorsToRemove
        )
      );
    },
    removeSituationFromTakeLocation: (
      state,
      action: PayloadAction<{
        takePositionId: string;
        fallbackCameraId: string;
        actorsToRemove: string[];
        propsToRemove: string[];
      }>
    ) => {
      applyStrategyPatch(state, "removeSituationFromTakeLocation", (strategy) =>
        strategy.removeSituationFromTakeLocation(
          state,
          action.payload.takePositionId,
          action.payload.fallbackCameraId,
          action.payload.actorsToRemove,
          action.payload.propsToRemove
        )
      );
    },
    // ====== Attachments =======
    addAttachmentToTake: (
      state,
      action: PayloadAction<{
        propId: string;
        actorId: string;
        tag: string;
        playerToProp?: boolean;
      }>
    ) => {
      applyStrategyPatch(state, "addAttachmentToTake", (strategy) =>
        strategy.addAttachmentToTake(
          state,
          action.payload.actorId,
          action.payload.propId,
          action.payload.tag,
          action.payload.playerToProp
        )
      );
    },
    removeAttachmentFromTake: (
      state,
      action: PayloadAction<{
        propId: string;
        actorId: string;
      }>
    ) => {
      applyStrategyPatch(state, "removeAttachmentFromTake", (strategy) =>
        strategy.removeAttachmentFromTake(
          state,
          action.payload.actorId,
          action.payload.propId
        )
      );
    },
    // 3D only: start the take the way the take before it ends, and take held
    // props off their positions (payload: takeId)
    matchTakeStartToPreviousTake: (state, action: PayloadAction<string>) => {
      const index = state.takes3D.findIndex((take) => take.id === action.payload);
      if (index < 0) return;

      const matched = matchTakeStartToPreviousTakeEnd(
        index > 0 ? state.takes3D[index - 1] : undefined,
        state.takes3D[index]
      );
      if (matched === state.takes3D[index]) return;

      state.takes3D[index] = matched;
      state.isDirty = true;
    },
    // ====== Props =======
    clearTraining: (state) => {
      Object.assign(state, {
        ...initialState,
        takes360: [],
        takes3D: [],
        actors: [],
        props: [],
        selectedEnvironmentLayerIds: [],
        sequenceVariables: [],
      });
    },
    // ====== Sequence Variables =======
    setSequenceVariables: (state, action: PayloadAction<Variable[]>) => {
      state.sequenceVariables = normalizeSequenceVariables(
        action.payload,
        state.actors
      );
      state.isDirty = true;
    },
    addSequenceVariable: (state, action: PayloadAction<Variable>) => {
      if (
        isReservedSequenceVariableId(action.payload.id, state.actors) ||
        isReservedSequenceVariableName(action.payload.name, state.actors)
      ) {
        return;
      }
      if (
        state.sequenceVariables.some(
          (variable) =>
            variable.id === action.payload.id ||
            variable.name === action.payload.name
        )
      ) {
        return;
      }
      state.sequenceVariables.push(action.payload);
      state.isDirty = true;
    },
    updateSequenceVariable: (
      state,
      action: PayloadAction<{ variableId: string; variable: Variable }>
    ) => {
      const index = state.sequenceVariables.findIndex(
        (v) => v.id === action.payload.variableId
      );
      if (index !== -1) {
        const existingVariable = state.sequenceVariables[index];
        if (isReservedSequenceVariableId(existingVariable.id, state.actors)) {
          return;
        }
        if (
          isReservedSequenceVariableId(action.payload.variable.id, state.actors) ||
          isReservedSequenceVariableName(
            action.payload.variable.name,
            state.actors
          )
        ) {
          return;
        }
        const hasNameConflict = state.sequenceVariables.some(
          (variable) =>
            variable.id !== existingVariable.id &&
            variable.name === action.payload.variable.name
        );
        if (hasNameConflict) {
          return;
        }
        state.sequenceVariables[index] = {
          ...action.payload.variable,
          id: existingVariable.id,
        };
        state.isDirty = true;
      }
    },
    deleteSequenceVariable: (state, action: PayloadAction<string>) => {
      if (isReservedSequenceVariableId(action.payload, state.actors)) {
        return;
      }
      state.sequenceVariables = state.sequenceVariables.filter(
        (v) => v.id !== action.payload
      );
      state.isDirty = true;
    },
    loadSequenceVariables: (state, action: PayloadAction<Variable[]>) => {
      state.sequenceVariables = normalizeSequenceVariables(
        action.payload,
        state.actors
      );
    },
  },
});

export const {
  createNewTraining,
  setTrainingName,
  setTrainingDescription,
  setDisableProgressBar,
  loadDisableProgressBar,
  setTrainingType,
  loadTrainingMetadata,
  setSelectedTake,
  setSelectedInteraction,
  toggleInteractionSelection,
  selectInteractionRange,
  setSelectedTakeSequence,
  setIsPlaying,
  addNewTake,
  addTakeCopy,
  addInteractionToTake,
  addInteractionToSelectedTake,
  addInteractionCopy,
  addInteractionToQuizFeedback,
  reorderQuizFeedbackInteractions,
  deleteInteraction,
  setInteractionsDisabled,
  setFilter,
  reorderTakes,
  reorderInteractions,
  moveInteraction,
  moveInteractions,
  setPrimaryInteraction,
  setSelectedInteractions,
  moveSequenceSelection,
  updateSelectedTake,
  updateSelectedTakeProperty,
  updateSelectedInteraction,
  updateSelectedInteractionProperty,
  updateSelectedTakeSequence,
  setViewportBounds,
  setImageBounds,
  replaceTakesFromUnity,
  hydrateTakes,
  deleteTake,
  clearTakes,
  setReferenceLanguage,
  loadReferenceLanguage,
  setIsDirty,
  resetIsDirty,
  loadActors,
  addActor,
  updateActor,
  loadPropPresets,
  addPropPreset,
  addEnvironmentPropPresetToAllTakes,
  deletePropPreset,
  setSelectedEnvironmentLayerIds,
  loadSelectedEnvironmentLayerIds,
  setSelectedPropPreset,
  setSelectedActor,
  deleteActor,
  updateSelectedActorState,
  addPropStateToSelectedTake,
  removePropStateFromSelectedTake,
  updateSelectedPropPreset,
  updatePropPresetPrefabNames,
  addSituationToTakeLocation,
  removeSituationFromTakeLocation,
  addAttachmentToTake,
  removeAttachmentFromTake,
  matchTakeStartToPreviousTake,
  clearTraining,
  setSequenceVariables,
  addSequenceVariable,
  updateSequenceVariable,
  deleteSequenceVariable,
  loadSequenceVariables,
} = trainingSlice.actions;

// selectors
export const selectTrainingName = (state: RootState) =>
  state.training.trainingName;
export const selectTrainingType = (state: RootState) =>
  state.training.trainingType;
export const selectTrainingDescription = (state: RootState) =>
  state.training.trainingDescription;
export const selectTakes = (state: RootState) => {
  return state.training.trainingType === TrainingType.Training360
    ? state.training.takes360 ?? []
    : state.training.takes3D ?? [];
};
export const selectTakes360 = (state: RootState) => state.training.takes360 ?? [];
export const selectTakes3D = (state: RootState) => state.training.takes3D ?? [];

export const selectFilteredTakes = createSelector(
  [
    (state: RootState) => state.training.takes360,
    (state: RootState) => state.training.filter,
  ],
  (takes, filter) => {
    return takes.filter((take) => {
      return take.name.toLowerCase().includes(filter.toLowerCase());
    });
  }
);

export const selectTakesMap = (state: RootState) => state.training.takes360;

export const selectSelectedTakeId = (state: RootState) =>
  state.training.selectedTake;

export const selectSelectedTake = (state: RootState) => {
  const takes360 = state.training.takes360 ?? [];
  return takes360.find(
    (take) => take.id === state.training.selectedTake
  );
};

export const selectSelectedTake3D = (state: RootState) => {
  const takes3D = state.training.takes3D ?? [];
  return takes3D.find(
    (take) => take.id === state.training.selectedTake
  );
};

export const selectSelectedElementId = (state: RootState) =>
  state.training.selectedElementId;
export const selectSelectedElementIsTake = (state: RootState) =>
  state.training.selectedElementIsTake;

export const selectSelectedElementIsInteraction = (state: RootState) =>
  state.training.selectedElementIsInteraction;
const EMPTY_INTERACTION_SELECTION: string[] = [];
export const selectSelectedInteractionIds = (state: RootState) =>
  state.training.selectedInteractionIds ?? EMPTY_INTERACTION_SELECTION;
export const selectSelectedInteractions = createSelector(
  [selectTakes, selectSelectedElementId, selectSelectedInteractionIds],
  (takes: Take[], selectedElementId, selectedInteractionIds): Interaction[] => {
    if (selectedInteractionIds.length === 0) return [];
    // The primary defines the container (Take list or quiz feedback slot);
    // members are returned in container order.
    const container = resolveSelectionContainer(takes ?? [], selectedElementId);
    if (!container) return [];
    return container.interactions.filter((interaction) =>
      selectedInteractionIds.includes(interaction.id)
    );
  }
);
export const selectSelectionAnchorId = (state: RootState) =>
  state.training.selectionAnchorId ?? "";
export const selectSelectedInteractionCount = (state: RootState) =>
  selectSelectedInteractionIds(state).length;
export const selectSelectedSequenceTakeId = (state: RootState) =>
  state.training.selectedSequenceTakeId;
export const selectSelectedTakeSequence = (state: RootState) => {
  const sequenceTakeId =
    state.training.selectedSequenceTakeId || state.training.selectedTake;
  const take = (state.training.takes3D ?? []).find(
    (candidate) => candidate.id === sequenceTakeId
  );
  if (!take) return undefined;

  return getTake3DSequence(take);
};

export const selectSelectedElement = (
  state: RootState
): Take | Interaction | ActorState | undefined => {
  return runStrategySelector(state, "selectSelectedElement", (strategy) =>
    strategy.selectSelectedElement(state)
  );
};

export const selectFilter = (state: RootState) => state.training.filter;

export const selectIsTeleportAllowed = (takes: Take360[], tpId: string) => {
  const parent = takes.find((take) => take.teleportMarker.id === tpId);
  if (parent) {
    if (parent.automaticTransition) return false;
    const index = takes.findIndex((take) => take.id === parent.id);
    if (index > -1 && index < takes.length - 1) return true;
  }

  return false;
};

export const selectIsPlaying = (state: RootState) => state.training.isPlaying;
export const selectTestData = (state: RootState) => state.training.testData;

export const selectReferenceLanguage = (state: RootState) =>
  state.training.referenceLanguage;
export const selectIsDirty = (state: RootState) =>
  state.history ? selectDocumentIsDirty(state) : state.training.isDirty;

export const selectDisableProgressBar = (state: RootState) =>
  state.training.disableProgressBar;

export const selectViewportBounds = (state: RootState) =>
  state.training.viewportBounds;
export const selectImageBounds = (state: RootState) =>
  state.training.imageBounds;

export const selectActors = (state: RootState) => state.training.actors;
export const selectSelectedActorId = (state: RootState) =>
  state.training.selectedActor;
export const selectSelectedActor = (state: RootState) =>
  state.training.actors.find(
    (actor) => actor.id === state.training.selectedActor
  );
export const selectPlayerActor = (state: RootState) =>
  state.training.actors.find((actor) => actor.id === "player");

export const selectSelectedElementType = (state: RootState) =>
  runStrategySelector(state, "selectSelectedElementType", (strategy) =>
    strategy.selectSelectedElementType(state.training)
  );

export const selectAllPropPresets = (state: RootState) => state.training.props;
export const selectSelectedPropPresetId = (state: RootState) =>
  state.training.selectedProp;
export const selectEnvironmentPropPresetByMetadataId = (
  state: RootState,
  environmentMetadataId: string
) =>
  state.training.props.find(
    (prop) =>
      prop.propType === PropType.Environment &&
      prop.environmentMetadataId === environmentMetadataId
  );
export const selectEnvironmentPropMetadataIds = createSelector(
  [selectAllPropPresets],
  (props) =>
    props
      .filter(
        (prop) =>
          prop.propType === PropType.Environment && prop.environmentMetadataId
      )
      .map((prop) => prop.environmentMetadataId!)
);
export const selectEnvironmentPropPresetsByMetadataId = createSelector(
  [selectAllPropPresets],
  (props) =>
    props
      .filter(
        (prop) =>
          prop.propType === PropType.Environment && prop.environmentMetadataId
      )
      .reduce<Record<string, Prop>>((result, prop) => {
        result[prop.environmentMetadataId!] = prop;
        return result;
      }, {})
);
export const selectSelectedEnvironmentLayerIds = (state: RootState) =>
  state.training.selectedEnvironmentLayerIds;
export const selectAllSituationPropPresets = (state: RootState) =>
  state.training.props.filter((prop) => prop.propType === PropType.Situation);

export const selectIsCameraSelected = (state: RootState) => {
  const element = selectSelectedElement(state);
  if (!element) return false;

  return (
    (element as Interaction).type !== undefined &&
    (element as Interaction).type === InteractionType.SetCamera
  );
};

export const selectCustomSequenceVariables = (state: RootState) =>
  state.training.sequenceVariables;

export const selectSequenceVariables = createSelector(
  [(state: RootState) => state.training.actors, selectCustomSequenceVariables],
  (actors, customVariables) => [
    ...getReservedSequenceVariables(actors),
    ...customVariables,
  ]
);

export default trainingSlice.reducer;
